import { ActionIcon, Badge, Center, Checkbox, Group, Loader, Menu, Table, Text, Tooltip } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import {
  IconBrain,
  IconDots,
  IconDownload,
  IconFileBarcode,
  IconFileInfo,
  IconHelp,
  IconPencil,
  IconRefresh,
  IconTrashX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  downloadEmbeddingFile,
  DatasetsFilesInfo,
  DataSourcesType,
  safeDecodeURI,
  CanAccess,
  PRODUCTION_STAGE_DEV,
} from '@mygpt/app';

import FileIcon from '@/components/FileIcon';
import { useDatasetStore } from '@/store/dataset';
import { isFileStatusProcess, isFileStatusReady, isFileStatusFail, isFileStatusInvalid } from '@/utils/embed';
import { formatFileSize } from '@/utils/helper';
import { useGPTStore } from '@/store';

interface Props {
  item: DatasetsFilesInfo;
  type: DataSourcesType;
  selected: boolean;
  isFileType?: boolean;
  onSelectedChange: (shiftKey: boolean) => void;
  deleteFile: (id: string) => void;
  isFileDeleteLoading: (id: string) => boolean;
  onEditTitle: (item: DatasetsFilesInfo) => void;
  onRetrain: () => void;
  onAdvancedTrain: () => void;
}

export default (props: Props) => {
  const { t } = useTranslation();

  const { datasetId } = useParams();
  const { pathname } = useLocation();

  const { isCloning, setCloningTipOpened } = useDatasetStore();
  const { configInfo } = useGPTStore();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const supportAdvancedTrain = configInfo?.advanced_parser.some((item) => item.enabled);
  const showDatasetChunksButton = isLocalMode
    ? window.ENV?.GBASE_SUPPORT_FRONTEND_SHOW_DATASET_CHUNKS_BUTTON === 'true'
    : true;
  const isDevEnv = useMemo(() => PRODUCTION_STAGE_DEV || window.location.host.indexOf('localhost:') > -1, []);

  const navigate = useNavigate();
  const {
    deleteFile,
    isFileDeleteLoading,
    isFileType,
    type,
    item,
    onEditTitle,
    onRetrain,
    onSelectedChange,
    selected,
    onAdvancedTrain,
  } = props;

  const isIntegrationType = type === 'integration';

  // 根据当前列表项的状态，判断是否允许一些功能。
  // 如果是集成类型，并且文件处于非（已学习）状态，不允许使用更多菜单项功能。
  const isMoreActionsAllowed = useMemo(() => {
    if (!isIntegrationType) {
      return true;
    }
    return item.file_status === 'complete';
  }, [isIntegrationType, item.file_status, isFileStatusFail]);

  const deleteFileConfirm = (file: DatasetsFilesInfo) => {
    openConfirmModal({
      title: t('embeddings.documents.delete.title'),
      centered: true,
      children: (
        <Trans i18nKey="embeddings.documents.delete.text" values={{ name: file.filename }}>
          Are you sure want to delete
          <span className="break-all font-semibold">{file.filename}</span>
        </Trans>
      ),
      labels: { confirm: t('delete_btn'), cancel: t('alert_cancel') },
      onCancel: () => console.log('Cancel'),
      confirmProps: {
        color: 'red',
        onClick: () => deleteFile(file.id),
      },
    });
  };

  const getCharactersCount = () => {
    const isBefore = dayjs(item.updated_at).isBefore('2024-01-19');
    if (isFileType) {
      return !item.file_size && isBefore ? '-' : formatFileSize(item.file_size);
    } else {
      return !item.characters_count && isBefore ? '-' : item.characters_count;
    }
  };

  const formatLearnModel = (learnType: number) => {
    const model = configInfo?.advanced_parser.find((item) => item.value === learnType);
    if (learnType === 100) {
      // Markitdown模式时，文案需要支持多语言，因此不能使用后端环境变量里配置的label
      return t('embeddings.table.filter_by_model.normal');
    }
    return learnType > 0 ? model?.label || t('embeddings.table.filter_by_model.advanced') : '-';
  };

  return (
    <Table.Tr style={{ wordBreak: 'break-word' }}>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <Checkbox checked={selected} onChange={(e) => onSelectedChange(e.nativeEvent.shiftKey)} />

          <Tooltip
            label={item.file_type === 'html' ? safeDecodeURI(item.filename) : item.filename}
            multiline
            className="max-w-[500px]  break-all"
          >
            {item.file_type === 'html' || item.source_type === 'web_connector' ? (
              <Text size="sm" lineClamp={1} component="a" href={item.filename} target="_blank" rel="noreferrer">
                {safeDecodeURI(item.filename)}
              </Text>
            ) : (
              <Text size="sm" lineClamp={1}>
                {item.filename}
              </Text>
            )}
          </Tooltip>
        </Group>
      </Table.Td>
      <Table.Td>
        <Tooltip label={item.title} multiline className="max-w-[500px]  break-all">
          <Text size="sm" lineClamp={1}>
            {item.title}
          </Text>
        </Tooltip>
      </Table.Td>
      {isFileType && (
        <Table.Td className="text-center">
          <span className="text-[13px] text-gray-400">
            <FileIcon fileName={item.filename} />
          </span>
        </Table.Td>
      )}
      <Table.Td className="text-center">
        <span className="text-[13px] text-gray-400">{getCharactersCount()}</span>
      </Table.Td>
      <Table.Td>
        <Center>
          <span className="flex-none">
            {item.file_status === 'complete' && (
              <Badge color="teal">{t('embeddings.table.filter_by_status.learned')}</Badge>
            )}
            {isFileStatusProcess(item.file_status) && (
              <Badge color="blue" rightSection={<Loader color="white" size={10} />}>
                {t('embeddings.table.filter_by_status.learning')}
              </Badge>
            )}
            {isFileStatusReady(item.file_status) && (
              <Badge color="gray">{t('embeddings.table.filter_by_status.ready')}</Badge>
            )}
            {isFileStatusFail(item.file_status) && (
              <Tooltip
                label={item.failed_reason}
                disabled={!item.failed_reason}
                multiline
                withArrow
                className="max-w-[500px]"
              >
                <Badge color="red" rightSection={<IconHelp color="white" size={14} />}>
                  {t('embeddings.table.filter_by_status.failed')}
                </Badge>
              </Tooltip>
            )}
            {isFileStatusInvalid(item.file_status) && (
              <Tooltip
                label={item.failed_reason}
                disabled={!item.failed_reason}
                multiline
                withArrow
                className="max-w-[500px]"
              >
                <Badge color="orange">{t('embeddings.table.filter_by_status.skipped')}</Badge>
              </Tooltip>
            )}
          </span>
        </Center>
      </Table.Td>
      {isFileType && supportAdvancedTrain && (
        <Table.Td className="text-center whitespace-nowrap">{formatLearnModel(item.learn_type)}</Table.Td>
      )}
      <Table.Td className="text-center">
        <span>
          <span className="text-[13px] text-gray-400">{item.tokens}</span>
        </span>
      </Table.Td>
      <Table.Td className="text-center hidden sm:block">
        <span className="text-[13px] text-gray-400 text-nowrap">
          {dayjs(item.updated_at).format('MM/DD/YYYY HH:mm')}
        </span>
      </Table.Td>
      <Table.Td>
        <Group gap={5} wrap="nowrap">
          {isMoreActionsAllowed && (
            <Menu shadow="md">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <IconDots size="1.125rem" />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCloning) {
                      setCloningTipOpened(true);
                      return;
                    }
                    onEditTitle(item);
                  }}
                >
                  {t('edit')}
                </Menu.Item>
                {isDevEnv && (
                  <Menu.Item
                    leftSection={<IconFileInfo size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        `/debug/chunks?bot_id=${datasetId}&url=${encodeURIComponent(item.filename)}&file_id=${item.id}`
                      );
                    }}
                  >
                    {t('dataset.show_chunk')}
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCloning) {
                      setCloningTipOpened(true);
                      return;
                    }
                    onRetrain();
                  }}
                >
                  {/* {t('retrain')} */}
                  {/* onprem下，先将普通学习改成快速学习 */}
                  {t('embeddings.documents.quick_learning')}
                </Menu.Item>
                {supportAdvancedTrain && isFileType && (
                  <CanAccess accessKey="enable_multi_modal_parsing">
                    <Menu.Item
                      leftSection={<IconBrain size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCloning) {
                          setCloningTipOpened(true);
                          return;
                        }
                        onAdvancedTrain();
                      }}
                    >
                      {t('embeddings.documents.advanced_train.button')}
                    </Menu.Item>
                  </CanAccess>
                )}
                {isFileType && (
                  <Menu.Item
                    leftSection={<IconDownload size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadEmbeddingFile({ file_id: item.id, filename: item.filename });
                    }}
                  >
                    {t('download')}
                  </Menu.Item>
                )}
                {showDatasetChunksButton && (
                  <Menu.Item
                    leftSection={<IconFileBarcode size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (type === 'integration') {
                        navigate(`${pathname}/chunk/${item.id}?title=${item.filename}`);
                      } else {
                        navigate(`${pathname}/${item.id}?title=${item.filename}`);
                      }
                    }}
                  >
                    {t('dataset.chunk_title')}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}

          <Tooltip label={t('delete_btn')}>
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={() => {
                if (isCloning) {
                  setCloningTipOpened(true);
                  return;
                }
                deleteFileConfirm(item);
              }}
              loaderProps={{ size: 16 }}
              loading={isFileDeleteLoading(item.id)}
            >
              <IconTrashX color="red" size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
